import {
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TablePagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik, useFormikContext } from "formik";
import React from "react";

import NoDataImage from "@/assets/error/no-data-found.jpg";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import {
  HeaderTableCell,
  TableContainer,
  TableHeaderRow,
} from "@/components/molecules/Table/styled";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "@/constants/pagination";
import useGetExecutionLogs from "@/hooks/requests/executionLogs/use-get-execution-logs";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { ExecutionLogMessages } from "@/messages/campaigns/exception-log.messages";

import { ExecutionLogRow } from "./row";
import { EXECUTION_LOGS_TABLE_COLUMNS, getInitialFilterValues } from "../../constants";
import FiltersForm from "../Filter";

interface Props {
  messages: ExecutionLogMessages;
}

export function ExecutionLogsTable({ messages }: Props) {
  const { values } = useFormikContext<CampaignFormData>();
  const columns = EXECUTION_LOGS_TABLE_COLUMNS(messages);
  const pagination = usePagination();
  const _id = (values?.id || 0) as number;
  const { pageNumber, pageSize, changePage, extend } = pagination;
  const filter = useFilters({ initialValue: getInitialFilterValues(_id) });
  const { filters } = filter;
  const getExcLogs = useGetExecutionLogs({ filters, pageNumber, pageSize, _id });
  const { data, isLoading, error } = getExcLogs;
  const { noLogsFound } = messages;
  const initialValues = filters.slice(1);
  const shouldLoadNoLogsFound = !!error && !isLoading;

  return (
    <Card>
      <MDBox p={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            {messages.executionLogTitle}
          </MDTypography>

          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            onSubmit={filter.changeFilters}
          >
            <FiltersForm
              messages={messages}
              filters={filter.temporaryFilters}
              addNewFilter={filter.addNewTemporaryFilter}
              deleteFilter={filter.deleteTemporaryFilter}
              campaignId={_id}
            />
          </Formik>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            {data && data.items.length > 0 && (
              <>
                <TableContainer sx={{ width: "100%" }}>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableHeaderRow>
                        {columns.map((label, index) => (
                          <HeaderTableCell key={`${index} - ${label}`} width={"20%"}>
                            {label}
                          </HeaderTableCell>
                        ))}
                      </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                      {isLoading && (
                        <Grid container justifyContent="center">
                          <CircularProgress />
                        </Grid>
                      )}

                      {data.items.map((log, index) => (
                        <ExecutionLogRow key={`${index} - ${log.id}`} log={log} index={index} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {data && (
                  <TablePagination
                    labelRowsPerPage={messages.rowsPerPage}
                    rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    component="div"
                    count={data.totalCount}
                    onRowsPerPageChange={(e) => extend(Number(e.target.value))}
                    rowsPerPage={pageSize}
                    page={pageNumber - 1}
                    onPageChange={(_, pageNumber) => changePage(pageNumber)}
                  />
                )}
              </>
            )}
            {!!shouldLoadNoLogsFound && (
              <AlertBlock
                title={noLogsFound}
                description={error.message}
                src={NoDataImage}
                alt={noLogsFound}
              />
            )}
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
